import React, { forwardRef, useRef } from 'react';
import { InputAdornment, TextField, TextFieldProps, inputAdornmentClasses } from '@mui/material';
import { UseFormRegisterReturn } from 'react-hook-form';

type PrefixTextFieldProps = Omit<TextFieldProps, 'slotProps' | 'onChange'> &
  Pick<UseFormRegisterReturn, 'disabled' | 'required'> & {
    value: string;
    prefix: string;
    /**
     * Conventionally, the pattern, if provided, must start with the ^ caret.
     * Otherwise, the replacement will cause unpredictable behavior
     */
    prefixMatch?: RegExp;
    inputAttributes?: Record<string, string | number>;
    onChange?: (value: string) => void;
  };

export const PrefixTextField = forwardRef<HTMLDivElement, PrefixTextFieldProps>(
  ({ value = '', prefix, prefixMatch, onChange, inputAttributes, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const prefixedValue = ensurePrefix(e.target.value, true);
      return onChange?.(prefixedValue);
    };

    const handleAdornmentClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const textbox = e.currentTarget
        .closest('.MuiInputBase-root')
        ?.querySelector<HTMLInputElement>('.MuiInputBase-input');
      textbox?.focus();
    };

    const handleCopy: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
      const input = inputRef.current!;
      if (input.selectionStart) return;

      e.preventDefault();
      const textToCopy = `${prefix}${input.value.substring(0, input.selectionEnd!)}`;
      e.clipboardData.setData('text/plain', textToCopy);
    };

    const ensurePrefix = (input: string, shouldBePresent: boolean) => {
      if (shouldBePresent) {
        // Add prefix if not starting nor matching
        return !input.startsWith(prefix) && !prefixMatch?.test(input) ? `${prefix}${input}` : input;
      }

      // Remove prefix if it's present and should not be
      if (input.startsWith(prefix)) return input.slice(prefix.length);

      // Apply regex-based prefix removal if available
      return prefixMatch ? input.replace(prefixMatch, '') : input;
    };

    return (
      <TextField
        fullWidth
        autoComplete="off"
        variant="outlined"
        size="small"
        {...props}
        value={ensurePrefix(value, false)}
        ref={ref}
        inputRef={inputRef}
        onChange={handleChange}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  userSelect: 'none',
                }}
                onClick={handleAdornmentClick}
              >
                {prefix}
              </InputAdornment>
            ),
          },
          htmlInput: {
            ...inputAttributes,
            onCopy: handleCopy,
          },
        }}
        sx={{
          [`& .${inputAdornmentClasses.root}`]: {
            marginRight: 0.25,
          },
        }}
      />
    );
  }
);
